import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import toastr from "toastr";
import { AlertTriangle, Clock, Loader2, Lock, MapPin, ShieldCheck, Sparkles, Wallet } from "lucide-react";

import { loadStoreDetail, closeStoreDetail } from "../../store/storeSlices/storeDetailSlice";
import { initiateStoreBill } from "../../store/storeSlices/storeBillSlice";
import {
    formatINR,
    getMembershipFee,
    getMembershipGST,
    getMembershipTotal,
    membershipFeeText,
    MembershipSummary,
    getCashbackAmount,
    getRewardCashback,
} from "./VoucherCheckoutPanel";
import MembershipPage, { getProgramRewards } from "./MembershipPage";
import { getBestReward, getNextReward, nextRewardLabel, round2 } from "../../utils/rewardUtils";
import { saveCheckoutOrder } from "../../utils/checkoutOrder";
import { getMembershipStatus, getOwnedProgramIds } from "../../utils/membershipStatus";
import useGoerOneAuth from "../../hooks/useGoerOneAuth";
import { formatCountdown, parseBillParams, useCountdown } from "../../utils/storeBill";

const CHECKOUT_PATH = "/web/store-payment";
const LOGIN_PATH = "/login";

const getUserPhone = (user) => String(user?.mobile ?? user?.phone ?? "").replace(/\D/g, "").slice(-10);

// Same shape StoreDetailPage builds for the order's membership
const toOrderMembership = (sel) =>
    sel
        ? {
              membershipId: sel.program.program_id,
              name: sel.program.program_name,
              owned: sel.action === "use",
              action: sel.action,
              fee: ["buy", "renew", "upgrade"].includes(sel.action) ? Number(sel.program.membership_amount) || 0 : 0,
              maxCoinPercent: sel.program.max_coin_usage_percent ?? 100,
          }
        : null;

function Notice({ title, text, action }) {
    return (
        <div className="mx-auto mt-16 max-w-sm rounded-2xl border border-gray-100 bg-white p-6 text-center shadow-sm">
            <AlertTriangle className="mx-auto h-10 w-10 text-orange-500" />
            <h1 className="mt-3 text-lg font-bold text-gray-900">{title}</h1>
            <p className="mt-1 text-sm text-gray-500">{text}</p>
            {action}
        </div>
    );
}

export default function StoreBillScan() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const bill = useMemo(() => parseBillParams(searchParams), [searchParams]);
    const storeId = bill?.storeId;
    const remaining = useCountdown(bill?.expiresAt);
    const expired = !!bill && remaining <= 0;

    const { store, rewards = [], loading, error } = useSelector((s) => s.storeDetail);
    const initiating = useSelector((s) => s.storeBill.initiating);
    const { user, refreshUser } = useGoerOneAuth();
    useEffect(() => { refreshUser(); }, [refreshUser]);

    const membershipStatus = getMembershipStatus(user);
    const ownedProgramIds = getOwnedProgramIds(user);

    const [selectedMembership, setSelectedMembership] = useState(null); // { program, action }
    const [membershipOpen, setMembershipOpen] = useState(false);

    /* ── load the store (name + rewards) ── */
    useEffect(() => {
        if (storeId) dispatch(loadStoreDetail({ storeId }));
    }, [storeId, dispatch]);
    useEffect(() => () => dispatch(closeStoreDetail()), [dispatch]);

    /* ── use the customer's active membership straight away ── */
    useEffect(() => {
        if (selectedMembership) return;
        if (membershipStatus.state !== "active" || !membershipStatus.programId) return;
        setSelectedMembership({
            program: {
                program_id: membershipStatus.programId,
                program_name: membershipStatus.programName,
                membership_amount: 0,
                max_coin_usage_percent: membershipStatus.program?.max_coin_usage_percent,
            },
            action: "use",
        });
    }, [selectedMembership, membershipStatus.state, membershipStatus.programId, membershipStatus.programName]);

    /* ── no active membership: ask them to pick one once logged in and the store is loaded ── */
    useEffect(() => {
        if (user?.id && store?.store_name && !selectedMembership && membershipStatus.state !== "active") {
            setMembershipOpen(true);
        }
    }, [user?.id, store?.store_name, selectedMembership, membershipStatus.state]);

    const handleMembershipSelect = (program, action) => {
        setSelectedMembership({ program, action });
        setMembershipOpen(false);
    };

    // The store page handles these two; send the customer there
    const goToStorePage = () => {
        setMembershipOpen(false);
        toastr.info("Continue from the store page for this option.");
        navigate(`/web/store-details?storeId=${storeId}`);
    };

    /* ── amount is locked; offer + totals use the same logic as PayBillPanel ── */
    const membership = toOrderMembership(selectedMembership);
    const payRewards = selectedMembership ? getProgramRewards(rewards, selectedMembership.program) : [];
    const value = bill?.amount || 0;

    const best = getBestReward(payRewards, value);
    const applicableReward = best?.reward || null;
    const rewardId = applicableReward?.reward_id ?? null;
    const isFixed = best?.isFixed || false;
    const pct = best?.pct || 0;
    const cap = best?.cap || 0;
    const capApplied = best?.capApplied || false;
    const discount = best?.discount || 0;
    const payable = round2(value - discount);

    const cashbackAmount = applicableReward ? getCashbackAmount(getRewardCashback(applicableReward), value) || 0 : 0;

    const membershipFee = getMembershipFee(membership);
    const membershipGST = getMembershipGST(membership);
    const membershipTotal = getMembershipTotal(membership);
    const total = round2(payable + membershipTotal);

    const next = value > 0 && selectedMembership ? getNextReward(payRewards, value, discount, applicableReward) : null;
    const address = [store?.address, store?.cityName].filter(Boolean).join(", ");

    const wrongCustomer = !!bill?.phone && !!user?.id && getUserPhone(user) !== bill.phone;
    const canPay = !!user?.id && !!selectedMembership && !expired && !wrongCustomer && value > 0 && !initiating;

    const handleLogin = () =>
        navigate(LOGIN_PATH, { state: { from: `${window.location.pathname}${window.location.search}` } });

    const handlePay = async () => {
        if (!canPay) return;
        // tell the store the customer has started paying (POST /store-pay/initiate); stop here if the bill is no longer payable
        const res = await dispatch(initiateStoreBill(bill.ref));
        if (!initiateStoreBill.fulfilled.match(res)) {
            toastr.error(res.payload || "Unable to start the payment. Please try again.");
            return;
        }
        const order = {
            store: { id: storeId, name: store.store_name, address: store.address, cityName: store.cityName, logo: store.logo },
            successPath: `/web/store-details?storeId=${storeId}`,
            membership,
            phone: getUserPhone(user),
            paybill: {
                amount: value,
                discount,
                cashback: cashbackAmount,
                rewardId,
                rewardName: payRewards.find((r) => r.reward_id === rewardId)?.reward_name || "",
            },
            // StorePayment.jsx uses storeBill.ref as the order ref (sent to /loyalty/redeem and in the encrypted udf7)
            storeBill: { ref: bill.ref, invoice: bill.invoice, createdAt: bill.createdAt },
        };
        saveCheckoutOrder(order);
        navigate(CHECKOUT_PATH, { state: { order } });
    };

    /* ── guards ── */
    if (!bill) {
        return <Notice title="Invalid payment link" text="This QR code isn't valid. Ask the store to generate a new one." />;
    }
    if (expired) {
        return <Notice title="This QR has expired" text="This payment QR is no longer valid. Ask the store to generate a new one." />;
    }
    if (loading && !store?.store_name) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#0B67FC]" />
            </div>
        );
    }
    if (error || !store?.store_name) {
        return <Notice title="Store not found" text="We couldn't load this store. Please scan the QR again." />;
    }

    return (
        <div className="min-h-screen bg-gray-50/60 pb-28">
            <div className="mx-auto max-w-md">
                {/* Header: store + locked amount */}
                <div className="rounded-b-3xl bg-[#0B67FC] px-5 pb-6 pt-6 text-white">
                    <h1 className="truncate text-xl font-bold">{store.store_name}</h1>
                    {address && (
                        <p className="mt-0.5 flex items-start gap-1 text-xs text-white/80">
                            <MapPin size={12} className="mt-0.5 shrink-0" />
                            <span className="line-clamp-2">{address}</span>
                        </p>
                    )}

                    <div className="mt-7 flex items-center justify-center gap-2">
                        <span className="text-4xl font-bold">{formatINR(value)}</span>
                        <Lock size={18} className="text-white/70" aria-label="Amount set by the store" />
                    </div>
                    <p className="mt-1 text-center text-xs text-white/80">
                        Bill amount set by the store{bill.invoice ? ` · Invoice ${bill.invoice}` : ""}
                    </p>

                    <div className="mt-4 flex justify-center">
                        <span className="flex items-center gap-1.5 rounded-full bg-black/20 px-4 py-1.5 text-xs font-semibold">
                            <Clock size={13} /> Pay within {formatCountdown(remaining)}
                        </span>
                    </div>
                </div>

                <div className="space-y-4 px-5 py-5">
                    {/* Not logged in */}
                    {!user?.id && (
                        <div className="rounded-2xl border border-gray-100 bg-white p-5 text-center shadow-sm">
                            <p className="text-sm font-semibold text-gray-900">Log in to see your offer and pay</p>
                            <p className="mt-1 text-xs text-gray-500">Your membership discount is applied automatically after you log in.</p>
                            <button
                                onClick={handleLogin}
                                className="mt-4 w-full rounded-xl bg-[#0B67FC] py-3 text-sm font-bold text-white hover:bg-[#0954D0]"
                            >
                                Log in
                            </button>
                        </div>
                    )}

                    {/* Bill meant for someone else */}
                    {wrongCustomer && (
                        <div className="rounded-xl border border-orange-300 bg-orange-50 px-4 py-3 text-sm font-medium text-orange-800">
                            This bill was created for a different mobile number. Log in with the number you gave the store.
                        </div>
                    )}

                    {/* Membership picker fallback */}
                    {user?.id && !selectedMembership && (
                        <div className="rounded-2xl border border-gray-100 bg-white p-5 text-center shadow-sm">
                            <p className="text-sm font-semibold text-gray-900">A membership is needed to pay</p>
                            <button
                                onClick={() => setMembershipOpen(true)}
                                className="mt-3 w-full rounded-xl bg-[#0B67FC] py-3 text-sm font-bold text-white hover:bg-[#0954D0]"
                            >
                                Choose membership
                            </button>
                        </div>
                    )}

                    {user?.id && selectedMembership && (
                        <>
                            {!membership?.owned && (
                                <MembershipSummary membership={membership} onChange={() => setMembershipOpen(true)} />
                            )}

                            {cashbackAmount > 0 && (
                                <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800">
                                    <Wallet size={16} className="shrink-0" />
                                    Earn {formatINR(cashbackAmount)} cashback on this bill
                                </div>
                            )}

                            <div className="space-y-2.5 rounded-2xl border border-gray-100 bg-white p-4 text-sm shadow-sm">
                                {next?.reward && next.need > 0 && (
                                    <div className="rounded-xl border border-[#0B67FC]/20 bg-[#0B67FC]/5 px-4 py-3">
                                        <div className="flex items-start gap-2">
                                            <Sparkles size={16} className="mt-0.5 shrink-0 text-[#0B67FC]" />
                                            <div>
                                                <p className="text-sm font-bold text-[#062E6C]">Spend {formatINR(next.need)} more</p>
                                                <p className="mt-0.5 text-xs font-medium text-[#0954D0]">to get {nextRewardLabel(next)} OFF</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-center justify-between text-gray-700">
                                    <span>Bill amount</span>
                                    <span className="font-semibold text-gray-900">{formatINR(value)}</span>
                                </div>

                                {discount > 0 && (
                                    <div className="flex items-center justify-between text-emerald-700">
                                        <span>
                                            {isFixed ? "You save" : capApplied ? `You save (max ${formatINR(cap)})` : `You save (${pct}%)`}
                                        </span>
                                        <span className="font-semibold">-{formatINR(discount)}</span>
                                    </div>
                                )}

                                {membership && !membership.owned && (
                                    <>
                                        <div className="flex items-center justify-between text-gray-700">
                                            <span>Membership Fee</span>
                                            <span className={`font-semibold ${membershipFee > 0 ? "text-gray-900" : "text-emerald-700"}`}>
                                                {membershipFee > 0 ? formatINR(membershipFee) : membershipFeeText(membership)}
                                            </span>
                                        </div>
                                        {membershipFee > 0 && (
                                            <div className="flex items-center justify-between text-gray-700">
                                                <span>GST on Membership (18%)</span>
                                                <span className="font-semibold text-gray-900">{formatINR(membershipGST)}</span>
                                            </div>
                                        )}
                                    </>
                                )}

                                <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                                    <span className="text-gray-700">To pay</span>
                                    <span className="text-xl font-bold text-gray-900">{formatINR(total)}</span>
                                </div>

                                {cashbackAmount > 0 && (
                                    <div className="flex items-center justify-between text-emerald-700">
                                        <span>Cashback you'll earn</span>
                                        <span className="font-semibold">{formatINR(cashbackAmount)}</span>
                                    </div>
                                )}
                            </div>

                            {discount > 0 && (
                                <p className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                                    <Sparkles size={14} /> You're saving {formatINR(discount)} on this bill
                                </p>
                            )}
                        </>
                    )}

                    <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                        <ShieldCheck className="h-4 w-4" /> 100% secure payments
                    </p>
                </div>
            </div>

            {/* Sticky pay bar */}
            <div className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-100 bg-white px-5 py-4">
                <div className="mx-auto max-w-md">
                    <button
                        type="button"
                        onClick={handlePay}
                        disabled={!canPay}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B67FC] py-3.5 text-base font-bold text-white shadow-sm transition-all hover:bg-[#0954D0] active:scale-[0.99] disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none"
                    >
                        {initiating ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                        Pay ₹ {canPay || initiating ? total.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "--"}
                    </button>
                </div>
            </div>

            <MembershipPage
                open={membershipOpen}
                onClose={() => setMembershipOpen(false)}
                store={store}
                rewards={rewards}
                ownedProgramIds={ownedProgramIds}
                selectedProgramId={selectedMembership?.program.program_id}
                currentProgramId={membershipStatus.programId}
                currentState={membershipStatus.state}
                isFirstMembership={membershipStatus.state === "none"}
                onSelect={handleMembershipSelect}
                onRequestConsideration={goToStorePage}
                onHaveInvitation={goToStorePage}
            />
        </div>
    );
}